import React, {useMemo} from "react";
import ARIcon from "../../ARIcon";
import {useSelector} from "react-redux";
import RuleSwitcher from "../RuleSwitcher";
import {useHistory} from "react-router-dom";
import {getArrayOfId} from "../../../utils";
import {useTranslation} from "react-i18next";
import {PATHS} from "../../../route-dictionary";
import {CButton, CButtonGroup} from "@coreui/react";
import {withErrorBoundary} from "../../ErrorBoundary";
import {globalSelector} from "../../../store/selectors/global";
import useCheckIdParams from "../../../pages/rules/hooks/useCheckIdParams";
import {showModalWindow, showNotification} from "../../../utils/actionWrappers";
import {compareElements, convertTimeToWordTime, getDayType, getStringFromArray, restoreArray} from "./RulesInfo.model";
import "./RuleInfo.scss";

const RuleInfo = (props) => {
  const {
    rule,
    showEditButton,
    showDeleteButton,
    showRuleSwitcher,
    updateStates,
    members,
    telegramChats,
    updateRule,
    deleteRule
  } = props;

  const {t} = useTranslation();
  const history = useHistory();
  const {isFetching} = useSelector(globalSelector);
  const {isChecksRulePage, checkId} = useCheckIdParams();

  const showActionButtons = showEditButton || showDeleteButton || showRuleSwitcher;

  const correctedRule = useMemo(() => ({
    ...rule,
    members_to_call: restoreArray(
      compareElements,
      rule.members_to_call,
      members
    ),
    telegram_chats: restoreArray(
      compareElements,
      rule.telegram_chats,
      telegramChats
    )
  }), [members, rule, telegramChats]);

  const deleteRuleHandler = () => {
    showModalWindow.confirm({
      message: t("modal:message.delete.rule", "Are you sure you want to remove this rule?"),
      color: "danger",
      onConfirm: () => deleteRule(rule.id)
        .then(() => {
          showNotification(
            t("notification:title.success", "Success!"),
            t("notification:message.deleted.rule", "Rule has been deleted."),
            "success"
          );
          updateStates && updateStates();
        })
    });
  };

  const onEditButtonClick = () => {
    history.push(
      isChecksRulePage ?
        PATHS.CHECKS.getChecksRuleDetailsPath({checkId, ruleId: rule.id}) :
        PATHS.RULES.getDetailsIdPath(rule.id)
    );
  };

  const switchRuleHandler = () => {
    const newRuleData = {
      ...rule,
      is_enabled: !rule.is_enabled,
      members_to_call: getArrayOfId(rule.members_to_call),
      telegram_chats: getArrayOfId(rule.telegram_chats)
    };

    return updateRule(newRuleData, rule.id, false)
      .then(() => updateStates && updateStates());
  };

  return (
    <div className="RulesInfo__content">
      <p className="RulesInfo__content-text">
        {!!correctedRule.from_time || !!correctedRule.to_time
          ? <>
            {t(
              "rule.time.fromTill",
              `From ${correctedRule.from_time} till ${correctedRule.to_time}`,
              {fromTime: correctedRule.from_time, toTime: correctedRule.to_time}
            )} {getDayType(correctedRule.day_type)}
          </>
          : <>{t("rule.time.anyTime")} {getDayType(correctedRule.day_type)}</>
        }
        <br/>
        {convertTimeToWordTime("after", correctedRule.delay)} {convertTimeToWordTime("every", correctedRule.recur)}
        <br/>
        {!!correctedRule.telegram_chats.length &&
        <>{t("rule.chats", "Chats")}: {getStringFromArray(correctedRule.telegram_chats, "chats")} <br/></>
        }
        {!!correctedRule.phones.length &&
        <>{t("rule.phones", "Phones")}: {getStringFromArray(correctedRule.phones, "phones")} <br/></>
        }
        {!!correctedRule.members_to_call.length &&
        <>{t("rule.membersToCall", "Members to call")}: {getStringFromArray(correctedRule.members_to_call, "members")}</>
        }
      </p>
      {
        showActionButtons &&
        <div className="RulesInfo__action-buttons-wrapper">
          {
            showRuleSwitcher &&
            <RuleSwitcher
              disabled={isFetching}
              checked={rule.is_enabled}
              switcherHandler={switchRuleHandler}
              className="RulesInfo__rule-switcher"
            />
          }
          {
            (showEditButton || showDeleteButton) &&
            <CButtonGroup className="RulesInfo__action-buttons">
              {
                showEditButton &&
                <CButton
                  className="RulesInfo__edit-button"
                  color="info"
                  variant="outline"
                  onClick={onEditButtonClick}
                  disabled={isFetching}
                >
                  {t("button.edit")}
                </CButton>
              }
              {
                showDeleteButton &&
                <CButton
                  className="RulesInfo__delete-button"
                  color="danger"
                  variant="outline"
                  onClick={deleteRuleHandler}
                  disabled={isFetching}
                >
                  <ARIcon name="cilTrash"/>
                </CButton>
              }
            </CButtonGroup>
          }
        </div>
      }
    </div>
  );
};

export default withErrorBoundary(RuleInfo);